/**
 * Table export: every row that matches a table's current search, filters and sort, as a CSV file a
 * spreadsheet opens directly. Pure except `saveTextFile`; the CSV and paging rules are unit-tested
 * (tests/unit/tableExport.test.mjs).
 *
 * - A server-paged table hands `collectAll` a loader for one page of its own query (page numbers
 *   via `byPageNumber`, or a cursor) and gets every matching row, not just those loaded so far.
 * - A table the API returns whole exports the rows its filters and sort left, all of them.
 *
 * The file: UTF-8 with a byte-order mark (so Excel reads ₹ and Indian-language names right),
 * CRLF lines, RFC 4180 quoting. Amounts are plain numbers (112.00) so they add up in a sheet;
 * times are the IST wall time the app shows ("2026-10-19 14:05").
 */

export type CellValue = string | number | boolean | null | undefined;

export interface ExportColumn<T> {
  header: string;
  value: (row: T) => CellValue;
}

/** The most rows one export collects: past it, the file says so and the table asks to narrow. */
export const EXPORT_MAX_ROWS = 50_000;
/** Rows per request while collecting: the API's largest page. */
export const EXPORT_PAGE_SIZE = 100;

// Text a spreadsheet would run as a formula (= + - @, or a leading tab / carriage return) is
// written as text with a leading apostrophe, so a typed name or note can't become a formula (CSV
// injection). A plain number such as -12.50 stays a number.
const FORMULA_START = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

/** One CSV cell. */
export function csvCell(value: CellValue): string {
  if (value === null || value === undefined) return "";
  let text = typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
  if (FORMULA_START.test(text) && !PLAIN_NUMBER.test(text)) text = `'${text}`;
  const quote = /[",\r\n]/.test(text) || text !== text.trim();
  return quote ? `"${text.replace(/"/g, '""')}"` : text;
}

/** The whole file: byte-order mark, a header row, then a row per item. */
export function toCsv<T>(rows: readonly T[], columns: readonly ExportColumn<T>[]): string {
  const lines = [columns.map((c) => csvCell(c.header)).join(",")];
  for (const row of rows) lines.push(columns.map((c) => csvCell(c.value(row))).join(","));
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

/** An API timestamp ("2026-10-19T14:05:09.123+05:30") as the IST wall time it carries:
 *  "2026-10-19 14:05" ("…14:05:09" with `seconds`). Anything else (a plain date, empty) passes
 *  through. */
export function sheetDateTime(iso: string | null | undefined, seconds = false): string {
  if (!iso) return "";
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(iso)) return iso;
  return `${iso.slice(0, 10)} ${iso.slice(11, seconds && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(iso) ? 19 : 16)}`;
}

/** The date part of an API timestamp or date ("2026-10-19"). */
export function sheetDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : "";
}

/** A code as words: "pending_verification" → "Pending verification". */
export function sheetLabel(code: string | null | undefined): string {
  if (!code) return "";
  const words = code.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A phone for a sheet: "+919876543210" → "98765 43210". Written with its "+91" a spreadsheet
 *  reads it as a formula (or shows 9.19877E+11); every number here is Indian. A masked one
 *  ("XXXXXXXXX3210") is left as it is. */
export function sheetPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const full = phone.replace(/\s+/g, "").match(/^\+91(\d{5})(\d{5})$/);
  return full ? `${full[1]} ${full[2]}` : phone;
}

/** "medicines-2026-10-19-1405.csv": the table, and when (IST) it was exported. */
export function exportFileName(table: string, now: Date = new Date()): string {
  const slug = table.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "export";
  const ist = new Date(now.getTime() + 330 * 60_000).toISOString(); // IST = UTC+5:30, no DST
  return `${slug}-${ist.slice(0, 10)}-${ist.slice(11, 13)}${ist.slice(14, 16)}.csv`;
}

// ------------------------------------------------------------------ collecting every page

/** One page of a list: its rows, what to ask for next (a page number or a cursor; none at the
 *  end), and how many rows match in all when the API says. */
export interface PageOf<T> {
  rows: T[];
  next: string | number | null | undefined;
  total?: number;
}

export interface CollectOptions<T> {
  maxRows?: number;
  signal?: AbortSignal;
  /** Rows so far, and the total when known. */
  onProgress?: (loaded: number, total: number | undefined) => void;
  /** A row's identity: a row seen on an earlier page (the list shifted while paging) is skipped. */
  keyOf?: (row: T) => string;
}

export interface Collected<T> {
  rows: T[];
  /** More rows matched than `maxRows`. */
  truncated: boolean;
  total: number | undefined;
}

export class ExportCancelled extends Error {
  constructor() {
    super("Export cancelled.");
    this.name = "ExportCancelled";
  }
}

/** Every row of a paged list, asking for one page at a time until the list says there is no more
 *  (or `maxRows` is reached). Throws `ExportCancelled` once `signal` aborts. */
export async function collectAll<T>(
  fetchPage: (next: string | number | undefined) => Promise<PageOf<T>>,
  { maxRows = EXPORT_MAX_ROWS, signal, onProgress, keyOf }: CollectOptions<T> = {},
): Promise<Collected<T>> {
  const rows: T[] = [];
  const seen = new Set<string>();
  let next: string | number | undefined;
  let total: number | undefined;
  for (;;) {
    if (signal?.aborted) throw new ExportCancelled();
    const page = await fetchPage(next);
    if (signal?.aborted) throw new ExportCancelled();
    total = page.total ?? total;
    for (const row of page.rows) {
      if (keyOf) {
        const key = keyOf(row);
        if (seen.has(key)) continue;
        seen.add(key);
      }
      if (rows.length === maxRows) return { rows, truncated: true, total };
      rows.push(row);
    }
    onProgress?.(rows.length, total);
    const more = page.next !== null && page.next !== undefined && page.next !== next;
    // A page with no rows ends it too, whatever it says: a list can't make progress that way.
    if (!more || page.rows.length === 0) return { rows, truncated: false, total };
    if (rows.length === maxRows) return { rows, truncated: true, total };
    next = page.next ?? undefined;
  }
}

/** A page-numbered list endpoint ({data, page, has_more, total}) as pages for `collectAll`. */
export function byPageNumber<T>(
  load: (page: number) => Promise<{ data: T[]; page: number; has_more: boolean; total?: number }>,
): (next: string | number | undefined) => Promise<PageOf<T>> {
  return async (next) => {
    const res = await load(typeof next === "number" ? next : 1);
    return { rows: res.data, next: res.has_more ? res.page + 1 : null, total: res.total };
  };
}

/** Hands the browser a text file to save. */
export function saveTextFile(text: string, fileName: string, type = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Later, not at once: some browsers are still reading the file when click() returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
